import { cn } from '@/lib/utils';
import {
  StatusGetInstances,
  StatusGetInstancesLabel,
} from '@/service/one-click-deploy/type';
import { CheckCircledIcon, CrossCircledIcon } from '@radix-ui/react-icons';
import { CircleIcon } from 'lucide-react';

interface Process {
  status: StatusGetInstances;
  label: string;
  description: string;
  variant: string;
}

type Props = {
  status?: StatusGetInstances;
  stateDeploy: StatusGetInstances;
};
const DeploymentTimeline = ({ status, stateDeploy }: Props) => {
  const process: Process[] = [
    {
      status: StatusGetInstances.Initial,
      label: StatusGetInstancesLabel[StatusGetInstances.Initial],
      description: '',
      variant: 'success',
    },
    {
      status: StatusGetInstances.Allocate,
      label: StatusGetInstancesLabel[StatusGetInstances.Allocate],
      description: '',
      variant: 'processing',
    },
    {
      status: StatusGetInstances.Running,
      label: StatusGetInstancesLabel[StatusGetInstances.Running],
      description: '',
      variant: '',
    },
  ];

  const updateProcessVariants = (
    listProcess: Process[],
    currentStatus: StatusGetInstances,
    hasError: boolean
  ): Process[] => {
    const lastIndex = listProcess.length - 1;
    let found = false;
    return listProcess.map((process, index) => {
      if (hasError) return { ...process, variant: 'error' }; // ใส่รอมี stateDeploy จริงๆ
      if (process.status === currentStatus) {
        found = true;
        if (index === lastIndex) {
          return { ...process, variant: 'success' };
        } else if (hasError) {
          return { ...process, variant: 'error' };
        } else {
          return { ...process, variant: 'processing' };
        }
      } else if (found) {
        return { ...process, variant: '' };
      } else {
        return { ...process, variant: 'success' };
      }
    });
  };

  const listProcess = updateProcessVariants(
    process,
    stateDeploy,
    status === StatusGetInstances.Failed
  );
  return (
    <div className="flex grid-cols-12 flex-col md:grid">
      {listProcess.map((process, index) => (
        <div key={index} className="flex md:contents">
          <div className="relative col-start-1 col-end-3 mr-10 md:mx-auto">
            <div className="flex h-full w-6 items-center justify-center">
              <div
                className={cn(
                  'pointer-events-none h-full w-1 bg-gray-400',
                  process.variant === 'processing' && 'bg-tory',
                  process.variant === 'success' && 'bg-green-600',
                  process.variant === 'error' && 'bg-destructive'
                )}
              ></div>
            </div>
            {process.variant === 'success' && (
              <CheckCircledIcon className="absolute top-1/2 m-auto -mt-3 h-6 w-6 rounded-full bg-green-600 text-center text-gray-50 shadow" />
            )}
            {process.variant === 'error' && (
              <CrossCircledIcon className="absolute top-1/2 m-auto -mt-3 h-6 w-6 rounded-full bg-destructive text-center text-gray-50 shadow" />
            )}
            {process.variant === 'processing' && (
              <div className="">
                <svg
                  aria-hidden="true"
                  role="variant"
                  className="absolute top-1/2 m-auto -mt-3 me-3 inline h-6 w-6 animate-spin rounded-full bg-tory p-0.5 text-center text-gray-50 shadow"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="#efefef52"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
            )}
            {!process.variant && (
              <CircleIcon className="absolute top-1/2 m-auto -mt-3 h-6 w-6 rounded-full bg-gray-400 text-center text-gray-50 shadow" />
            )}
          </div>
          <div className="col-start-3 col-end-12 my-8 mr-auto">
            <p
              className={cn(
                'text-base font-semibold',
                !process.status && 'text-muted-foreground'
              )}
            >
              {process.label}
            </p>
            {process.description && (
              <p className="mt-1 text-justify leading-tight text-muted-foreground">
                {process.description}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DeploymentTimeline;
